import { z } from "zod";

/** Evaluation workflow only: actor names are not authenticated permissions. */
export const QUERY_STATUSES = [
  "draft",
  "issued",
  "partially-answered",
  "ready-for-review",
  "closed",
] as const;
export const QUERY_PRIORITIES = ["Low", "Normal", "High", "Critical"] as const;
export type QueryStatus = (typeof QUERY_STATUSES)[number];
export type QueryPriority = (typeof QUERY_PRIORITIES)[number];

function validDay(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}
const required = z.string().trim().min(1, "Required field is missing.");
const day = z.string().refine(validDay, "Enter a valid date as YYYY-MM-DD.");
const messageSchema = z.object({
  id: required,
  author: required,
  at: z.string().datetime(),
  visibility: z.enum(["internal", "client"]),
  text: required,
  evidenceRef: z.string().default(""),
});
export type QueryMessage = z.infer<typeof messageSchema>;
export const querySchema = z.object({
  id: required,
  reference: required,
  title: required,
  description: required,
  area: required,
  priority: z.enum(QUERY_PRIORITIES),
  owner: required,
  clientContact: required,
  dueDate: day,
  status: z.enum(QUERY_STATUSES),
  procedureId: z.string().optional(),
  financialImpact: z.number().finite().nonnegative(),
  blocksCompletion: z.boolean(),
  messages: z.array(messageSchema),
  createdBy: required,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  closedBy: required.optional(),
  closedAt: z.string().datetime().optional(),
  closureConclusion: required.optional(),
  escalationReason: required.optional(),
});
export type AuditQuery = z.infer<typeof querySchema>;
export type NewQueryInput = Pick<
  AuditQuery,
  "title" | "description" | "area" | "owner" | "clientContact" | "dueDate"
> &
  Partial<
    Pick<
      AuditQuery,
      "priority" | "procedureId" | "financialImpact" | "blocksCompletion"
    >
  >;
export type NewQueryMessage = {
  text: string;
  evidenceRef?: string;
  visibility: QueryMessage["visibility"];
};

function identity(actor: string): string {
  if (!actor.trim()) throw new Error("An actor is required.");
  return actor.trim();
}
const sameActor = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();
function open(query: AuditQuery): void {
  if (query.status === "closed")
    throw new Error(
      "Reopen the closed query with a reason before changing it.",
    );
}
function note(query: AuditQuery, actor: string, text: string): AuditQuery {
  const at = new Date().toISOString();
  return {
    ...query,
    updatedAt: at,
    messages: [
      ...query.messages,
      {
        id: crypto.randomUUID(),
        author: identity(actor),
        at,
        visibility: "internal",
        text,
        evidenceRef: "",
      },
    ],
  };
}
function evidence(query: AuditQuery): QueryMessage[] {
  return query.messages.filter((message) => message.evidenceRef.trim());
}

export function newQuery(
  input: NewQueryInput,
  actor: string,
  existingCount: number,
): AuditQuery {
  if (
    !Number.isSafeInteger(existingCount) ||
    existingCount < 0 ||
    existingCount >= Number.MAX_SAFE_INTEGER
  )
    throw new Error(
      "Existing query count must be a non-negative safe integer.",
    );
  const at = new Date().toISOString();
  return querySchema.parse({
    ...input,
    id: crypto.randomUUID(),
    reference: `Q-${String(existingCount + 1).padStart(3, "0")}`,
    priority: input.priority ?? "Normal",
    financialImpact: input.financialImpact ?? 0,
    blocksCompletion: input.blocksCompletion ?? false,
    procedureId: input.procedureId?.trim() || undefined,
    status: "draft",
    messages: [],
    createdBy: identity(actor),
    createdAt: at,
    updatedAt: at,
  });
}

export function issueQuery(query: AuditQuery, actor: string): AuditQuery {
  identity(actor);
  open(query);
  if (query.status !== "draft")
    throw new Error("Only a draft query can be issued.");
  querySchema.parse(query);
  return note(
    { ...query, status: "issued" },
    actor,
    "Query issued in the evaluation register; no external message was sent.",
  );
}

export function addQueryMessage(
  query: AuditQuery,
  input: NewQueryMessage,
  actor: string,
): AuditQuery {
  open(query);
  if (query.status === "draft" && input.visibility === "client")
    throw new Error(
      "Issue the query before recording a client-visible response.",
    );
  const at = new Date().toISOString();
  const message = messageSchema.parse({
    ...input,
    id: crypto.randomUUID(),
    author: identity(actor),
    at,
    text: input.text.trim(),
    evidenceRef: input.evidenceRef?.trim() || "",
  });
  // New client responses or replacement evidence invalidate readiness for review.
  const invalidatesReview =
    query.status === "ready-for-review" &&
    (message.visibility === "client" || Boolean(message.evidenceRef));
  const status =
    (query.status === "issued" && message.visibility === "client") ||
    invalidatesReview
      ? "partially-answered"
      : query.status;
  const result = {
    ...query,
    status,
    updatedAt: at,
    messages: [...query.messages, message],
  };
  return invalidatesReview
    ? note(
        result,
        actor,
        "Review readiness withdrawn after a new response or evidence reference; reassess the complete response.",
      )
    : result;
}

export function changeQueryStatus(
  query: AuditQuery,
  status: QueryStatus,
  actor: string,
  reason?: string,
): AuditQuery {
  actor = identity(actor);
  open(query);
  if (status === query.status) return query;
  if (query.status === "draft" && status === "issued")
    return issueQuery(query, actor);
  if (query.status === "draft" || status === "draft")
    throw new Error(
      "Issue a draft first; issued queries cannot return to draft.",
    );
  const explanation = reason?.trim() || "";
  if (status === "closed") {
    if (query.status !== "ready-for-review")
      throw new Error(
        "Mark the query ready for review before independent closure.",
      );
    if (
      sameActor(actor, query.createdBy) ||
      query.messages.some(
        (m) =>
          (m.visibility === "client" || m.evidenceRef.trim()) &&
          sameActor(actor, m.author),
      )
    )
      throw new Error(
        "Closure requires an independent reviewer, different from the creator, responder and evidence preparer.",
      );
    if (explanation.replace(/\s/g, "").length < 20)
      throw new Error(
        "A substantive closure conclusion of at least 20 non-whitespace characters is required.",
      );
    if (!evidence(query).length)
      throw new Error("Linked evidence is required for closure.");
    const at = new Date().toISOString();
    return note(
      {
        ...query,
        status,
        closedBy: actor,
        closedAt: at,
        closureConclusion: explanation,
      },
      actor,
      `Query closed after independent review. Conclusion: ${explanation}. Evidence references: ${evidence(
        query,
      )
        .map((m) => m.evidenceRef)
        .join("; ")}`,
    );
  }
  if (status === "ready-for-review") {
    if (!evidence(query).length)
      throw new Error(
        "Link response evidence before marking the query ready for review.",
      );
    return note(
      { ...query, status },
      actor,
      "Complete response submitted for independent review.",
    );
  }
  if (status === "issued" || status === "partially-answered") {
    if (
      status === "partially-answered" &&
      !query.messages.some(
        (m) => m.visibility === "client" || m.evidenceRef.trim(),
      )
    )
      throw new Error(
        "Record a response or evidence before marking the query partially answered.",
      );
    if (!explanation)
      throw new Error(
        "Record the reason for returning the query for further response.",
      );
    return note(
      { ...query, status },
      actor,
      `Query returned to ${status}. Reason: ${explanation}`,
    );
  }
  throw new Error("Unsupported query status.");
}

export function reopenQuery(
  query: AuditQuery,
  actor: string,
  reason: string,
): AuditQuery {
  identity(actor);
  if (query.status !== "closed")
    throw new Error("Only a closed query can be reopened.");
  if (!reason.trim()) throw new Error("A reason for reopening is required.");
  const { closedBy, closedAt, closureConclusion, ...rest } = query;
  return note(
    { ...rest, status: "issued" },
    actor,
    `Query reopened. Reason: ${reason.trim()}. Prior closure by ${closedBy ?? "unrecorded"} at ${closedAt ?? "unrecorded"}; conclusion: ${closureConclusion ?? "unrecorded"}`,
  );
}

export function escalateQuery(
  query: AuditQuery,
  actor: string,
  reason: string,
): AuditQuery {
  identity(actor);
  open(query);
  if (!reason.trim()) throw new Error("A reason for escalation is required.");
  return note(
    { ...query, escalationReason: reason.trim() },
    actor,
    `Query escalated internally. Reason: ${reason.trim()}. No external notification was sent.`,
  );
}

function todayUtc(today?: string): string {
  const result = today ?? new Date().toISOString().slice(0, 10);
  if (!validDay(result))
    throw new Error("Today must be a valid UTC date as YYYY-MM-DD.");
  return result;
}
export function isQueryOverdue(query: AuditQuery, today?: string): boolean {
  const comparison = todayUtc(today);
  if (!validDay(query.dueDate)) return false;
  return (
    query.status !== "closed" &&
    query.status !== "draft" &&
    query.dueDate < comparison
  );
}
export function querySummary(queries: AuditQuery[], today?: string) {
  const comparison = todayUtc(today);
  const byStatus: Record<QueryStatus, number> = {
    draft: 0,
    issued: 0,
    "partially-answered": 0,
    "ready-for-review": 0,
    closed: 0,
  };
  for (const query of queries) byStatus[query.status]++;
  return {
    total: queries.length,
    open: queries.filter((q) => q.status !== "closed").length,
    overdue: queries.filter((q) => isQueryOverdue(q, comparison)).length,
    readyForReview: byStatus["ready-for-review"],
    closed: byStatus.closed,
    blocking: queries.filter((q) => q.status !== "closed" && q.blocksCompletion)
      .length,
    byStatus,
  };
}

function csv(rows: (string | number)[][]): string {
  return rows
    .map((row) =>
      row
        .map((value) => {
          const text = String(value);
          // Quoting alone does not prevent spreadsheet formula execution.
          const safe =
            /^[\s\uFEFF]*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text)
              ? `'${text}`
              : text;
          return `"${safe.replaceAll('"', '""')}"`;
        })
        .join(","),
    )
    .join("\r\n");
}

export function exportQueriesCsv(
  queries: AuditQuery[],
  options: { clientFacing: boolean },
): string {
  if (options.clientFacing) {
    // Explicit allowlist: do not spread an internal query/message into a client export.
    return csv([
      [
        "Reference",
        "Query",
        "Requested information",
        "Audit area",
        "Client contact",
        "Due date",
        "Shared conversation",
      ],
      ...queries
        .filter((query) => query.status !== "draft")
        .map((query) => [
          query.reference,
          query.title,
          query.description,
          query.area,
          query.clientContact,
          query.dueDate,
          query.messages
            .filter((m) => m.visibility === "client")
            .map((m) => `${m.at} | ${m.author}: ${m.text}`)
            .join("\n"),
        ]),
    ]);
  }
  return csv([
    [
      "Reference",
      "Query",
      "Description",
      "Area",
      "Priority",
      "Owner",
      "Client contact",
      "Due date",
      "Status",
      "Procedure",
      "Financial impact (not deduplicated)",
      "Blocks completion",
      "Created by",
      "Created at",
      "Updated at",
      "Closed by",
      "Closed at",
      "Closure conclusion",
      "Escalation reason",
      "Full history",
    ],
    ...queries.map((query) => [
      query.reference,
      query.title,
      query.description,
      query.area,
      query.priority,
      query.owner,
      query.clientContact,
      query.dueDate,
      query.status,
      query.procedureId || "",
      query.financialImpact,
      query.blocksCompletion ? "Yes" : "No",
      query.createdBy,
      query.createdAt,
      query.updatedAt,
      query.closedBy || "",
      query.closedAt || "",
      query.closureConclusion || "",
      query.escalationReason || "",
      query.messages
        .map(
          (m) =>
            `${m.at} | ${m.visibility} | ${m.author}: ${m.text}${m.evidenceRef ? ` [Evidence: ${m.evidenceRef}]` : ""}`,
        )
        .join("\n"),
    ]),
  ]);
}
